import { map, pick, isEmpty, chain, pluck, difference, findWhere, get } from 'underscore';
import { CampaignCriterion } from '@bingads-webui/campaign-odata-schemas';
import { modelToViewModelDayTimeTargets, locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import { getBundleCountriesAndBundleLocationCriterions, shouldSelectBundleOption, parseLocationTargets, convertLocationStateToLocationSchemaCampaign, getLocationIntent } from '@bingads-webui-campaign/hotel-common-targets';
import { minuteMap } from '../common/constants';

export const initialCampaignCriterionsViewModel = {
  data: [],
  isValid: true,
  isDirty: false,
};


const whitelistedSchemaPropertyKeys = Object.keys(CampaignCriterion.properties);

export const viewModelToModelForCampaignCriterions = (campaignCriterionsViewModel) => {
  const model = pick(campaignCriterionsViewModel.data, whitelistedSchemaPropertyKeys);
  return model;
};

export const modelToViewModelForCampaignCriterions = (campaignCriterionsModel) => {
  const data = pick(campaignCriterionsModel, whitelistedSchemaPropertyKeys);
  const campaignCriterionsViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return campaignCriterionsViewModel;
};

export const viewModelToBulkModelForCampaignCriterions = (campaignModel, campaignCriterionsViewModel) => {
  const campaignCriterionsModel = viewModelToModelForCampaignCriterions(campaignCriterionsViewModel);
  const campaignCriterionsBulkUpserModel = map(
    campaignCriterionsModel,
    campaignCriterionModel => ({ ...campaignCriterionModel, Campaign: { Id: campaignModel.Id } })
  );

  return campaignCriterionsBulkUpserModel;
};

export const modelToViewModelForLocationTargets = ({
  model, bundleCountries, campaignId,
}) => {
  const {
    locations,
    intentId,
    intentOption,
  } = parseLocationTargets(model, 'campaign', campaignId);
  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;
  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      bidMode: false,
      useParentLevel: locations.length === 0,
    },
  };
};

export const modelToViewModelForDayTimeTargets = ({
  model,
}) => {
  const campaignDayTimeRanges = modelToViewModelDayTimeTargets(model);
  return {
    adSchedules: {
      dayTimeRanges: campaignDayTimeRanges,
    },
  };
};

export const bulkModelToViewModelForDayTimeTargets = ({
  criterions,
}) => {
  const schedulingRows = [];

  chain(criterions)
    .filter(criterion => criterion.CriterionType === 'DayTime')
    .each((criterion) => {
      schedulingRows.push({
        Id: criterion.Id,
        Day: criterion.Criterion.DayTimeCriterion.Day,
        StartHour: criterion.Criterion.DayTimeCriterion.FromHour,
        StartMinute: minuteMap[criterion.Criterion.DayTimeCriterion.FromMinute],
        EndHour: criterion.Criterion.DayTimeCriterion.ToHour,
        EndMinute: minuteMap[criterion.Criterion.DayTimeCriterion.ToMinute],
      });
    })
    .value();

  return {
    adSchedules: {
      dayTimeRanges: schedulingRows,
    },
  };
};

export const modelToViewModelForCampaignNegativeWebpages = ({
  model,
}) => ({
  finalUrlExpansions: map(model, campaignNegativeWebpage => ({
    Id: campaignNegativeWebpage.Id,
    Criterion: {
      WebPage: {
        ...campaignNegativeWebpage.Criterion,
      },
    },
  })),
});

export const bulkModelToViewModelForCampaignNegativeWebpages = ({
  model,
}) => ({
  finalUrlExpansions:
  chain(model)
    .filter(campaignNegativeWebpage => campaignNegativeWebpage.Status !== 'Deleted')
    .map(campaignNegativeWebpage => ({
      Id: campaignNegativeWebpage.Id,
      Criterion: {
        ...campaignNegativeWebpage.Criterion,
      },
    }))
    .value(),
});

export const bulkModelToViewModelForLocationTargets = ({
  locationTargetsModel, bundleCountries,
}) => {
  const locations = locationTargetsModel
    .filter(criterion => criterion.Criterion.Type === 'Radius' || criterion.Criterion.Type === 'Location');
  const intent = locationTargetsModel.find(criterion => criterion.Criterion.Type === 'LocationIntentCriterion');
  const intentId = intent ? intent.Id : null;
  const intentOption = intent ? getLocationIntent(intent.Criterion.LocationIntentCriterion.IntentOption) : null;
  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;
  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      bidMode: false,
      useParentLevel: locations.length === 0,
    },
  };
};

export const viewModelToBulkModelForLocationTargets = (campaignViewModel, bundleLocationCriterions) => {
  const bulkUpsertModel = convertLocationStateToLocationSchemaCampaign(
    campaignViewModel.data.locationTargets,
    bundleLocationCriterions,
    {
      ...campaignViewModel.data,
      bidStrategy: {
        option: '',
      },
      name: {
        value: campaignViewModel.data.Name,
      },
    }
  );
  return bulkUpsertModel;
};

export const viewModelToBulkModelForAdSchedules = (campaignViewModel) => {
  const targets = campaignViewModel.data.adSchedulesBulkUpsert || [];
  return targets.map((criterion) => {
    const Status = isEmpty(criterion) ? 'Active' : criterion.Status;
    if (campaignViewModel.data.Id) {
      return {
        ...criterion,
        Status,
        Campaign: {
          Id: campaignViewModel.data.Id,
        },
      };
    }
    return {
      ...criterion,
      Status,
      CampaignName: campaignViewModel.data.Name,
    };
  });
};

export const viewModelToBulkModelFoFinalUrlExpansions = (campaignViewModel) => {
  let autoTargets = campaignViewModel.data.finalUrlExpansionsBulkUpsert || [];
  autoTargets = autoTargets
    .filter(target => target.Id || target.Status !== 'Deleted')
    .map((target) => {
      const Status = !target.Status ? 'Active' : target.Status;

      if (campaignViewModel.data.Id) {
        return {
          ...{
            Criterion: {
              ...target.Criterion,
              Type: 'NegativeWebpage',
            },
          },
          Status,
          Campaign: {
            Id: campaignViewModel.data.Id,
          },
          Id: target.Id,
        };
      }
      return {
        ...{
          Criterion: {
            ...target.Criterion,
            Type: 'NegativeWebpage',
          },
        },
        Status,
        CampaignName: campaignViewModel.data.Name,
        Id: target.Id,
      };
    });
  return {
    CampaignNegativeWebpages: autoTargets,
  };
};

export const viewModelToBulkModelForFinalUrlExpansions = (campaignViewModel, originalCampaignViewModel) => {
  // Ignore changes of final url exclusions if the option is opt-out.
  if (campaignViewModel.data.finalUrlExpansionOptOut) return {};

  // Workaround for keeping current final url implementation until the new control is fully released.
  if (!isEmpty(campaignViewModel.data.finalUrlExpansionsBulkUpsert)) return viewModelToBulkModelFoFinalUrlExpansions(campaignViewModel);

  const newFinalUrlExpansions = get(campaignViewModel, ['data', 'finalUrlExpansions'], []);
  const originalFinalUrlExpansions = get(originalCampaignViewModel, ['data', 'finalUrlExpansions'], []);

  function transformObject(target) {
    const status = !target.Status ? 'Active' : target.Status;
    const baseObject = {
      Criterion: {
        ...target.Criterion,
        Type: 'NegativeWebpage',
      },
      Status: status,
      Id: target.Id,
    };
    if (campaignViewModel.data.Id) {
      return { ...baseObject, Campaign: { Id: campaignViewModel.data.Id } };
    }
    return { ...baseObject, CampaignName: campaignViewModel.data.Name };
  }

  const newIds = pluck(newFinalUrlExpansions, 'Id');
  const originalIds = pluck(originalFinalUrlExpansions, 'Id');
  const deletedIds = difference(originalIds, newIds);

  const deletedTargets = deletedIds.map((id) => {
    const target = findWhere(originalFinalUrlExpansions, { Id: id });
    return transformObject({ ...target, Status: 'Deleted' });
  });
  const addedTargets = newFinalUrlExpansions.filter(target => !target.Id).map(transformObject);

  return {
    CampaignNegativeWebpages: [
      ...deletedTargets,
      ...addedTargets,
    ],
  };
};

export const loadBundleCountries = ({
  cid,
  aid,
  serverContext,
}) => {
  const lcid = serverContext ? serverContext.CurrentUser.LocaleID : '1033';
  const market = serverContext ? serverContext.CurrentUser.Market : 'US';
  const language = 'All';

  return getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, language, market);
};
